import { useCallback, useMemo, useRef } from 'react';
import { rollCharacteristicTest, buildTestNarrative } from '../engine/characteristicTests';
import { effectiveSheet } from '../engine/roster/stancedSheet';
import { getConditionTestModifier } from '../engine/conditionModifiers';
import { projectDefender } from '../engine/roster/projection';
import { infoLine, mergeAll } from './shellCombatant';

/** One undoable condition laid on the actor, and its line. */
function layOn(code, line, deps) {
  const { actor, updateCombatantState, addRollNarrative, capturePreActionSnapshot, pushHistory, stackPolicy } = deps;
  capturePreActionSnapshot?.();
  pushHistory?.();
  updateCombatantState(actor.id, (st) => ({ ...st, conditions: mergeAll(st.conditions ?? [], [code], stackPolicy) }));
  addRollNarrative(infoLine(line));
}

/**
 * His conditions' penalty to this characteristic's tests, through conditionModifiers.ts as every test takes it (the
 * parity rule; the review found Jump and Leap without it) - nothing when the table switched condition modifiers off.
 */
function conditionsOn(charId, { actor, applyConditionModifiers, condModOptions }) {
  if (applyConditionModifiers === false) return undefined;
  const mod = getConditionTestModifier(projectDefender(actor), { characteristic: charId }, condModOptions);
  return mod.total ? { label: mod.labels.join(', '), value: mod.total } : undefined;
}

/** Jump (Strength, standing) or Leap (Agility, running): the test, its degrees, and the GM's call on distance. */
function jumpTest(charId, word, deps) {
  const { actor, addRollNarrative, unnaturalDosMode } = deps;
  const name = actor.name || actor.sheet?.name || 'The combatant';
  const extraMod = conditionsOn(charId, deps);
  // The GM's Unnatural badge override counts here as on the sheet's own test of it (the review).
  const multiplierOverride = deps.attackerUnnaturalOverrides?.[charId];
  const opts = { mode: unnaturalDosMode, multiplierOverride, ...(extraMod ? { extraMod } : {}) };
  const result = rollCharacteristicTest(effectiveSheet(actor), charId, 0, 0, opts);
  const structured = buildTestNarrative(result, name);
  const text = `${structured.summary} - a ${word}: the GM judges how far.`;
  addRollNarrative({ type: 'test-result', text, structured: { ...structured, summary: text }, timestamp: Date.now() });
}

const PLATES = {
  goProne: (d) => layOn('P', `⬇ ${d.actor.name} drops prone (Free Action).`, d),
  tacticalAdvance: (d) =>
    layOn('Ta', `🛡 ${d.actor.name} makes a Tactical Advance: cover to cover, counted as in cover while he moves.`, d),
  disengage: (d) => layOn('Dg', `↩ ${d.actor.name} disengages from melee: no free strike as he goes.`, d),
  jump: (d) => jumpTest('str', 'standing jump', d),
  leap: (d) => jumpTest('agi', 'running leap', d),
};

/**
 * useShellMovementPlates - the drum's movement plates (2026-10-26): Go Prone (Free), Tactical Advance, Disengage,
 * Jump and Leap (Full Actions). Each is an `instant` plate: a condition laid on the actor with its line (undoable),
 * or a characteristic test whose distance the GM judges. Jumping, like moving, is not attacking: it loses a held aim
 * through the move it is part of.
 *
 * @param {object} s - actor, updateCombatantState, addRollNarrative, capturePreActionSnapshot, pushHistory,
 *   stackPolicy, unnaturalDosMode, applyConditionModifiers and condModOptions (the table's condition rules),
 *   attackerUnnaturalOverrides (the sheet badges' ×N).
 * @returns {Record<string, () => void>} the instant actions, by plate id
 */
export default function useShellMovementPlates(s) {
  const live = useRef(s);
  live.current = s;
  const press = useCallback((id) => {
    if (live.current.actor) PLATES[id](live.current);
  }, []);
  return useMemo(() => Object.fromEntries(Object.keys(PLATES).map((id) => [id, () => press(id)])), [press]);
}
